import React, { useMemo, useState, useRef, useEffect } from 'react';
import { PlayCircleV2, XV2 } from '@piiqtechnologies/svg-icons';
import { styled, PeekDialog, PeekBox } from '@piiqtechnologies/ui-components';
import { SLIDE_SHOWN_DWELL_MS } from '../../constants.js';
import { isShowableItem, getWhatsNewDialog, getSlideWords, fullHeadlineOf } from '../../dialogs/catalog.js';
import { resolveSlideTreatments } from '../../treatments.js';
import { NotificationCard } from '../notification-card/NotificationCard.js';
import { NotificationCardSurface, CardEyebrow, CardHeadline } from '../notification-card/cardStyles.js';
import { REDUCED_MOTION, viewEnterMotion, prefersMotion, SLIDE_EXIT_MS } from '../notification-card/motion.js';

// The pop-up (What's New API contract, Confluence: the pop-up and its events): everything waiting
// for this page, one slide each, in one dialog, drawn from the pop-up template (Figma "02 — POP-UP
// TEMPLATES"). "Next" walks the slides and "Close" is on the last one; the X in the card's corner
// and Esc close it from any slide. All three dismiss the slide on screen; the slides after it were
// never reached, so they come back next time. The backdrop does nothing. A slide counts as seen
// ("shown") once it has been on screen for SLIDE_SHOWN_DWELL_MS; one closed sooner isn't reported.
// "Watch demo" plays the demo in place, still the same slide; its X, and Esc, go back to the slide.
// "Next" hands over with a crossfade (see notification-card/motion). MUI's modal traps focus inside
// and gives it back to where it was on close.
const CARD_WIDTH = 479;
const DEMO_WIDTH = 793;
const HEADING_ID = "whats-new-dialog-heading";
const DESCRIPTION_ID = "whats-new-dialog-description";
const visuallyHidden = {
    position: "absolute",
    width: 1,
    height: 1,
    overflow: "hidden",
    clip: "rect(0 0 0 0)",
    whiteSpace: "nowrap",
};
const CloseButton = styled("button")({
    position: "absolute",
    top: 21,
    right: 21,
    width: 24,
    height: 24,
    padding: 0,
    border: 0,
    background: "none",
    color: "#ffffff",
    cursor: "pointer",
    "& svg": { width: 24, height: 24 },
    "&:focus-visible": { outline: "2px solid #ffffff", outlineOffset: 2 },
});
/** Under the buttons: one dot per slide, the one on screen lit; a screen reader hears "Update 2 of 3". */
const SlideProgress = ({ slides, index }) => {
    return (React.createElement(PeekBox, { "data-testid": "whats-new-slide-progress", sx: { display: "flex", alignItems: "center", justifyContent: "center" } },
        React.createElement(PeekBox, { "aria-hidden": true, sx: { display: "flex", gap: "10px", flexShrink: 0 } }, slides.map((slide, dot) => (React.createElement(PeekBox, { key: slide.id, sx: {
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: dot === index ? "#ffffff" : "rgba(255, 255, 255, 0.3)",
                transition: "background-color 300ms ease",
            } })))),
        React.createElement(PeekBox, { component: "span", "aria-live": "polite", sx: visuallyHidden }, `Update ${index + 1} of ${slides.length}`)));
};
/** What a Storylane embed posts to the page on each step; `demo_finished` once the last one is done. */
const isStorylaneFinished = (data) => {
    var _a;
    const message = data;
    return (message === null || message === void 0 ? void 0 : message.message) === "storylane-demo-event" && ((_a = message.payload) === null || _a === void 0 ? void 0 : _a.event) === "demo_finished";
};
/** "Watch demo" plays the Storylane demo in place, in the same dialog, instead of navigating away. Its
 * X goes back to the slide, so the slides after it are still shown. Painted as the template's "DEMO …"
 * frame for the slide's treatment; the play button shows until the demo draws over it. */
const DemoPlayer = ({ slide, treatment, onClose, onFinished }) => {
    var _a;
    const iframeRef = useRef(null);
    const onFinishedRef = useRef(onFinished);
    onFinishedRef.current = onFinished;
    // Only this demo's own frame is listened to, whichever site it is served from.
    useEffect(() => {
        let finished = false;
        const handleMessage = (event) => {
            var _a, _b;
            if (finished || event.source !== ((_a = iframeRef.current) === null || _a === void 0 ? void 0 : _a.contentWindow) || !isStorylaneFinished(event.data))
                return;
            finished = true;
            (_b = onFinishedRef.current) === null || _b === void 0 ? void 0 : _b.call(onFinishedRef, slide);
        };
        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, [slide]);
    const dialog = getWhatsNewDialog(slide.template);
    const words = dialog && getSlideWords(slide, dialog);
    const headline = words ? fullHeadlineOf(words) : "";
    const { demo } = treatment;
    return (React.createElement(NotificationCardSurface, { paint: demo, treatmentId: treatment.id, "data-testid": "whats-new-demo-player" },
        React.createElement(PeekBox, { sx: { position: "relative", display: "flex", flexDirection: "column", gap: "8px", px: "10px", pr: 5 } },
            React.createElement(CardEyebrow, { icon: React.createElement(PlayCircleV2, null), color: demo.eyebrow }, "Demo video"),
            words && React.createElement(CardHeadline, { id: HEADING_ID, words: words, color: "#ffffff", highlight: demo.highlight })),
        React.createElement(PeekBox, { sx: {
                position: "relative",
                height: 379,
                maxHeight: "60vh",
                overflow: "hidden",
                borderRadius: "8px",
                backgroundColor: "#14141e",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: demo.highlight,
            } },
            React.createElement(PlayCircleV2, { "aria-hidden": true, width: 49, height: 49, strokeWidth: 0.875 }),
            React.createElement("iframe", { ref: iframeRef, src: (_a = slide.cta) === null || _a === void 0 ? void 0 : _a.href, title: `Demo: ${headline}`, allow: "autoplay; fullscreen", allowFullScreen: true, style: { position: "absolute", inset: 0, width: "100%", height: "100%", border: 0, display: "block" } })),
        React.createElement(CloseButton, { type: "button", "aria-label": "Close the demo", onClick: onClose },
            React.createElement(XV2, null))));
};
const NotificationDialog = ({ open, slides, onPrimary, onDismiss, onSlideShown, onDemoFinished, startWithDemo = false, }) => {
    var _a, _b;
    // A pop-up published before this build knew it is skipped; with none left, there is no pop-up.
    const renderable = useMemo(() => slides.filter(isShowableItem), [slides]);
    const treatments = useMemo(() => resolveSlideTreatments(renderable), [renderable]);
    const [index, setIndex] = useState(0);
    // Only a slide whose button is a demo has one to start on.
    const firstDemo = ((_b = (_a = renderable[0]) === null || _a === void 0 ? void 0 : _a.cta) === null || _b === void 0 ? void 0 : _b.variant) === "watch_demo" ? renderable[0] : null;
    const demoToStartWith = open && startWithDemo ? firstDemo : null;
    const [demoSlide, setDemoSlide] = useState(demoToStartWith);
    // The slide "Next" just replaced, fading out over the new one inside the card.
    const [leaving, setLeaving] = useState(null);
    // Once "Next" was pressed, each new slide's picture and words come in (and play out in full: the
    // card keys them by slide, so they animate once, when they appear).
    const [animated, setAnimated] = useState(false);
    const leavingTimerRef = useRef();
    useEffect(() => () => window.clearTimeout(leavingTimerRef.current), []);
    // Each time the pop-up opens, or is handed new slides, it starts again from the first slide. The
    // reset happens while rendering, not in an effect, so the slide the last pop-up was left on is
    // never painted, or reported as shown, before it.
    const [shownFor, setShownFor] = useState({ open, slides: renderable });
    if (shownFor.open !== open || shownFor.slides !== renderable) {
        setShownFor({ open, slides: renderable });
        if (open) {
            setIndex(0);
            setDemoSlide(demoToStartWith);
            setLeaving(null);
            setAnimated(false);
        }
    }
    const current = renderable[Math.min(index, renderable.length - 1)];
    // A slide is reported once it has been on screen for the dwell: the clock starts again with each
    // slide, keeps running while its demo plays, and stops if the pop-up closes first.
    const onSlideShownRef = useRef(onSlideShown);
    onSlideShownRef.current = onSlideShown;
    useEffect(() => {
        if (!open || !current)
            return;
        const timer = window.setTimeout(() => onSlideShownRef.current(current.id), SLIDE_SHOWN_DWELL_MS);
        return () => window.clearTimeout(timer);
    }, [open, current]);
    if (!current)
        return null;
    const treatment = treatments[Math.min(index, treatments.length - 1)];
    const isLast = index >= renderable.length - 1;
    const handlePrimary = (slide) => {
        var _a;
        // A slide still coming in (Next pressed twice, with the buttons moved under the pointer as the
        // card changed height) isn't acted on before it is seen.
        if (leaving)
            return;
        onPrimary(slide);
        if (((_a = slide.cta) === null || _a === void 0 ? void 0 : _a.variant) === "watch_demo")
            setDemoSlide(slide);
    };
    const handleSecondary = (slide) => {
        // A second press while a slide is still coming in (a double-click) would skip, or close on, a
        // slide the customer never got to see.
        if (leaving)
            return;
        if (isLast) {
            onDismiss(slide);
            return;
        }
        if (prefersMotion()) {
            setAnimated(true);
            setLeaving({ slide, index });
            window.clearTimeout(leavingTimerRef.current);
            leavingTimerRef.current = window.setTimeout(() => setLeaving(null), SLIDE_EXIT_MS);
        }
        setIndex((value) => Math.min(value + 1, renderable.length - 1));
    };
    // The demo player's X goes back to the slide; one the pop-up opened on (from the drawer) closes it.
    const closeDemo = () => {
        if (demoToStartWith && demoSlide === demoToStartWith)
            onDismiss(demoSlide);
        else
            setDemoSlide(null);
    };
    // Esc does what the X on screen does.
    const handleEscape = () => {
        if (demoSlide)
            closeDemo();
        else
            onDismiss(current);
    };
    const leavingSlide = leaving && renderable.includes(leaving.slide) ? leaving : null;
    return (React.createElement(PeekDialog, { open: open, 
        // The backdrop does nothing: a stray click outside shouldn't close an announcement unread.
        onClose: (_event, reason) => {
            if (reason === "escapeKeyDown")
                handleEscape();
        }, maxWidth: false, "aria-labelledby": HEADING_ID, "aria-describedby": demoSlide ? undefined : DESCRIPTION_ID, PaperProps: {
            "data-testid": "whats-new-dialog",
            sx: {
                width: "100%",
                maxWidth: demoSlide ? DEMO_WIDTH : CARD_WIDTH,
                m: 2,
                backgroundColor: "transparent",
                backgroundImage: "none",
                boxShadow: "none",
                borderRadius: "12px",
                transition: "max-width 320ms cubic-bezier(0.22, 1, 0.36, 1)",
                [REDUCED_MOTION]: { transition: "none" },
            },
        } },
        React.createElement(PeekBox, { key: demoSlide ? "demo" : "card", sx: viewEnterMotion }, demoSlide ? (React.createElement(DemoPlayer, { slide: demoSlide, treatment: treatment, onClose: closeDemo, onFinished: onDemoFinished })) : (React.createElement(NotificationCard, { slide: current, treatment: treatment, onPrimary: handlePrimary, onSecondary: handleSecondary, secondaryLabel: isLast ? "Close" : "Next", onClose: onDismiss, headingId: HEADING_ID, descriptionId: DESCRIPTION_ID, footer: renderable.length > 1 && React.createElement(SlideProgress, { slides: renderable, index: index }), animateIn: animated, previous: leavingSlide ? { slide: leavingSlide.slide, treatment: treatments[leavingSlide.index] } : undefined })))));
};

export { NotificationDialog, NotificationDialog as default };
